import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Badge,
  Card,
  Row,
  Col,
  Form,
  InputGroup,
  Alert,
} from "react-bootstrap";
import {
  Search,
  Eye,
  RefreshCw,
  Package,
  Calendar,
  FileText,
  Printer,
  Download,
} from "lucide-react";
import useAuth from "../../hooks/useAuth";
import {
  // Méthodes pour Admin/Manager
  getAllDeliveryNotes,
  getDeliveryNotesByStatus,
  // Nouvelles méthodes pour Distributeur
  getMyDeliveryNotes,
  getMyDeliveryNotesByStatus,
  getDeliveryNotesByManager
} from "../../api/orders";
import GenererPdfBonLivraison from "./GenererPdfBonLivraison";
import { createRoot } from "react-dom/client";

const DeliveryNoteList = ({ onViewDeliveryNote, onCreateDeliveryNote }) => {
  const { isAdmin, isManager, isEmployee, isDistributor, user } = useAuth();
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [filteredDeliveryNotes, setFilteredDeliveryNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  // Configuration de l'entreprise (à adapter selon votre configuration)
  const company = {
    name: "SENEGAL SOPLAST SARL",
    address: {
      street: "Zone Industrielle de Thiès",
      city: "Thiès, Dakar, Sénégal",
    },
    phone1: "+221 33 888 88 88",
  };

  useEffect(() => {
    const fetchDeliveryNotes = async () => {
        try {
            setLoading(true);
            setError('');
            let response;

            if (isDistributor) {
                response = selectedStatus !== 'ALL'
                    ? await getMyDeliveryNotesByStatus(selectedStatus)
                    : await getMyDeliveryNotes();
            } else if (isManager && !isAdmin) {
                // Gestionnaire : uniquement les BL de ses magasins (statut filtré côté client)
                response = await getDeliveryNotesByManager(user.id);
            } else {
                // Admin
                response = selectedStatus !== 'ALL'
                    ? await getDeliveryNotesByStatus(selectedStatus)
                    : await getAllDeliveryNotes();
            }

            const data = Array.isArray(response) ? response : response.data;
            setDeliveryNotes(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Échec du chargement des bons de livraison. Veuillez réessayer.');
            console.error(err);
            setDeliveryNotes([]);
        } finally {
            setLoading(false);
        }
    };
    if (user) fetchDeliveryNotes();
}, [refreshTrigger, selectedStatus, isAdmin, isManager, isDistributor, user]);


  useEffect(() => {
    let filtered = Array.isArray(deliveryNotes) ? [...deliveryNotes] : [];

    if (selectedStatus !== 'ALL') {
        filtered = filtered.filter(dn => dn.orderStatus === selectedStatus);
    }
    if (searchTerm.trim() !== '') {
        const t = searchTerm.toLowerCase();
        filtered = filtered.filter(dn =>
            dn.referenceNumber?.toLowerCase().includes(t) ||
            dn.orderNumber?.toLowerCase().includes(t)
        );
    }
    setFilteredDeliveryNotes(filtered);
}, [deliveryNotes, searchTerm, selectedStatus]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // --- Téléchargement du PDF du BL à la demande, façon InvoiceList ---
  // On ne génère le PDF qu'au clic, dans un noeud détaché, puis on nettoie.
  const handleDownloadBL = (note) => {
    try {
      const pdfElement = document.createElement("div");
      pdfElement.style.position = "absolute";
      pdfElement.style.left = "-9999px";
      document.body.appendChild(pdfElement);

      const root = createRoot(pdfElement);
      root.render(
        <GenererPdfBonLivraison
          deliveryNote={note}
          company={company}
          onDownload={(blob) => {
            try {
              if (!(blob instanceof Blob)) {
                console.error("Error: Not a valid Blob object", blob);
                return;
              }
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `BL_${note.referenceNumber}.pdf`;
              document.body.appendChild(link);
              link.click();

              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              document.body.removeChild(pdfElement);
              root.unmount();
            } catch (error) {
              console.error("Erreur lors du téléchargement du BL:", error);
            }
          }}
        />
      );
    } catch (err) {
      console.error("Erreur lors du téléchargement du BL:", err);
      setError("Impossible de télécharger le bon de livraison");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
        return <Badge bg="warning">En attente</Badge>;
      case "CONFIRMED":
        return <Badge bg="info">Confirmée</Badge>;
      case "DELIVERED":
        return <Badge bg="success">Livrée</Badge>;
      case "REJECTED":
        return <Badge bg="danger">Rejetée</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      //hour: "2-digit",
      //minute: "2-digit",
    });
  };

  // Liste triée (réutilisée tableau + cartes)
  const sortedNotes = [...filteredDeliveryNotes].sort(
    (a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate),
  );

  // Actions réutilisées (tableau lg + cartes mobile)
  const renderActions = (note) => (
    <div className="d-flex gap-2 flex-wrap justify-content-end align-items-center">
      <Button
        variant="outline-info"
        size="sm"
        onClick={() => onViewDeliveryNote(note.id)}
        title="Détails"
      >
        <Eye size={16} />
      </Button>

      {/* Téléchargement PDF à la demande (généré seulement au clic) */}
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => handleDownloadBL(note)}
        title="Télécharger le PDF"
      >
        <Download size={16} />
      </Button>
    </div>
  );

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Row className="align-items-center g-2">
          <Col xs={12} sm>
            <h5 className="mb-0">
              {isDistributor
                ? "Mes Bons de Livraison"
                : "Gestion des Bons de Livraison"}
            </h5>
          </Col>
          <Col xs={12} sm="auto">
            {/* Le bouton "Nouveau Bon de Livraison" n'est affiché que pour Admin/Manager */}
            {!isDistributor && (
              <Button
                variant="success"
                size="sm"
                className="me-2"
                onClick={onCreateDeliveryNote}
              >
                <FileText size={16} className="me-1" />
                Nouveau Bon de Livraison
              </Button>
            )}

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleRefresh}
            >
              <RefreshCw size={16} />
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="mb-3 g-2">
          <Col xs={12} md={8}>
            <InputGroup>
              <InputGroup.Text>
                <Search size={18} />
              </InputGroup.Text>
              <Form.Control
                placeholder={
                  isDistributor
                    ? "Rechercher dans mes bons de livraison..."
                    : "Rechercher par référence, commande ou client..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          {/* <Col md={4}>
                        <Form.Select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                            <option value="ALL">Tous les statuts</option>
                            <option value="PENDING">En attente</option>
                            <option value="IN_TRANSIT">En transit</option>
                            <option value="DELIVERED">Livré</option>
                            <option value="RETURNED">Retourné</option>
                        </Form.Select>
                    </Col> */}
        </Row>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-2">
              {isDistributor
                ? "Chargement de vos bons de livraison..."
                : "Bons de livraison en chargement..."}
            </p>
          </div>
        ) : sortedNotes.length === 0 ? (
          <div className="text-center py-4 text-muted">
            {isDistributor
              ? "Aucun bon de livraison trouvé dans votre compte."
              : "Aucun bon de livraison trouvé"}
          </div>
        ) : (
          <>
            {/* ≥ lg : tableau */}
            <div className="table-responsive d-none d-lg-block">
              <Table striped hover className="mb-0">
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Commande</th>
                    {!isDistributor && <th>Client</th>}
                    <th>Magazin</th>
                    <th>Produits</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedNotes.map((note) => (
                    <tr key={note.id}>
                      <td>
                        <div className="fw-bold">{note.referenceNumber}</div>
                        <small className="text-muted">
                          {formatDate(note.deliveryDate)}
                        </small>
                      </td>
                      <td>
                        <div className="fw-bold">{note.orderNumber}</div>
                        {note.orderStatus && (
                          <div className="mt-1">
                            {getStatusBadge(note.orderStatus)}
                          </div>
                        )}
                      </td>
                      {!isDistributor && (
                        <td>
                          <div>{note.clientName}</div>
                        </td>
                      )}
                      <td>
                        <div className="small d-flex align-items-center">
                          {note.magasinName}
                        </div>
                        <small className="text-muted">
                          {note.magazinCode}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <Package size={12} className="me-1" />
                          {note.items?.length || 0} produits
                        </div>
                      </td>
                      <td>{renderActions(note)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* < lg : cartes */}
            <div className="d-lg-none">
              {sortedNotes.map((note) => (
                <Card key={note.id} className="mb-2 shadow-sm">
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <div className="fw-bold">{note.referenceNumber}</div>
                        <small className="text-muted">
                          {formatDate(note.createdAt)}
                        </small>
                      </div>
                      {note.orderStatus && getStatusBadge(note.orderStatus)}
                    </div>

                    <div className="small mb-1">
                      <span className="text-muted">Commande : </span>
                      {note.orderNumber}
                    </div>

                    {!isDistributor && (
                      <div className="small mb-1">
                        <span className="text-muted">Client : </span>
                        {note.clientName || "—"}
                      </div>
                    )}

                    <div className="small mb-1">
                      <span className="text-muted">Magasin : </span>
                      {note.magasinName}
                      {note.magazinCode ? ` (${note.magazinCode})` : ""}
                    </div>

                    <div className="small mb-3 d-flex align-items-center">
                      <Package size={12} className="me-1" />
                      {note.items?.length || 0} produits
                    </div>

                    {renderActions(note)}
                  </Card.Body>
                </Card>
              ))}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default DeliveryNoteList;